import { Box, Card, CardActionArea, CardContent, Stack, Typography } from '@mui/material';
import { useGetUserDeviceGroupsLastSeen } from 'gen/client';
import { UserUIDeviceGroupResponseDTO } from 'gen/model';
import { MouseEvent, ReactNode, useMemo } from 'react';

import getActivityColor from '../../Utils/getActivityColor';
import DeviceImage from '../Avatars/DeviceImage';
import { dayjs } from '../Dayjs/dayjs-utils';
import NavigationLink from '../Link/NavigationLink';

interface DeviceCardProps {
    actionButton?: ReactNode;
    currentDevice?: boolean;
    deviceGroup: UserUIDeviceGroupResponseDTO;
    deviceImage?: string;
    linkTo?: string;
}

export default function DeviceCard({
    actionButton,
    currentDevice = false,
    deviceGroup,
    deviceImage,
    linkTo,
}: Readonly<DeviceCardProps>) {
    const { data: onlineDevs } = useGetUserDeviceGroupsLastSeen(
        {},
        {
            swr: {
                refreshInterval: 60_000,
            },
        },
    );

    const [lastSeenDevice, lastSeenDeviceMinute] = useMemo(() => {
        const today = dayjs();

        if (!onlineDevs) {
            return [null, null];
        }
        const activeDevice = onlineDevs.filter((dev) => deviceGroup.id === dev.group_id);

        if (activeDevice.length === 0) {
            return [null, null];
        }

        const selectActiveDevice = activeDevice[0]!;

        const lastsmns = dayjs.duration(today.diff(selectActiveDevice.timestamp)).asMinutes();
        return [selectActiveDevice, lastsmns];
    }, [onlineDevs, deviceGroup.id]);

    // Stop propagation of click event on action button to prevent card click
    const handleActionButtonAreaClick = (e: MouseEvent<HTMLDivElement>) => {
        if (e.target instanceof Node) {
            const actionButtonArea = e.currentTarget;
            if (actionButtonArea.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();
            }
        }
    };

    const cardContent = (
        <NavigationLink to={linkTo}>
            <CardActionArea>
                {/* Header with image and action button */}
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        p: 2,
                        width: '100%',
                    }}
                >
                    <DeviceImage desktop={!deviceGroup.devicespec_id} imageName={deviceImage} size={115} />
                </Stack>

                {/* Content with device name and status */}
                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        justifyContent: 'space-between',
                        pb: 2,
                        pt: 0,
                        px: 2,
                    }}
                >
                    <Typography
                        sx={{
                            color: currentDevice ? 'primary.main' : 'inherit',
                            fontSize: 18,
                            fontWeight: 'medium',
                            mb: 1,
                            overflow: 'hidden',
                            textAlign: 'left',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        variant="h6"
                    >
                        {currentDevice ? (
                            <Typography
                                component="span"
                                sx={{
                                    backgroundColor: 'primary.main',
                                    borderRadius: 1,
                                    color: 'primary.contrastText',
                                    fontSize: 11,
                                    fontWeight: 'bold',
                                    letterSpacing: 0.5,
                                    mr: 1,
                                    px: 1,
                                    py: 0.25,
                                    textTransform: 'uppercase',
                                }}
                            >
                                Current
                            </Typography>
                        ) : null}
                        {deviceGroup.group_name}
                    </Typography>

                    {!deviceGroup.visible && (
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                fontSize: 14,
                                fontStyle: 'italic',
                                mb: 1,
                            }}
                        >
                            Device tracking disabled
                        </Typography>
                    )}

                    {/* Status and location info */}
                    <Stack spacing={1} sx={{ mt: 'auto' }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 'medium',
                                }}
                                variant="body2"
                            >
                                Location:
                            </Typography>
                            <Typography variant="body2">{lastSeenDevice?.instance_role ?? 'Unknown'}</Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 'medium',
                                }}
                                variant="body2"
                            >
                                Status:
                            </Typography>
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                <Box
                                    sx={{
                                        backgroundColor: getActivityColor({
                                            busy: false,
                                            lastSeenMinutes: lastSeenDeviceMinute,
                                        }),
                                        borderRadius: '50%',
                                        height: 10,
                                        width: 10,
                                    }}
                                />
                                <Typography variant="body2">
                                    {lastSeenDevice ? dayjs(lastSeenDevice.timestamp).fromNow() : 'Unknown'}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </CardActionArea>
        </NavigationLink>
    );

    return (
        <Card
            elevation={deviceGroup.visible ? 8 : 4}
            sx={{
                borderLeft: `8px solid ${getActivityColor({ busy: false, lastSeenMinutes: lastSeenDeviceMinute })}`,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
            }}
        >
            {actionButton ? (
                <Box
                    onClick={handleActionButtonAreaClick}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                        p: 1,
                        position: 'absolute',
                        right: 5,
                        top: 5,
                        zIndex: 2,
                    }}
                >
                    {actionButton}
                </Box>
            ) : null}
            {cardContent}
        </Card>
    );
}
